'use client'

import { Bell, FileText, PhoneCall, Star } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  useMyInvestorProfile,
  useUpdateMyNotificationPreferences,
  type UpdateNotificationPreferencesInput,
} from '@/features/investors'

type NotificationKey = keyof UpdateNotificationPreferencesInput

const NOTIFICATION_TOGGLES: {
  key: NotificationKey
  label: string
  description: string
  icon: LucideIcon
}[] = [
  {
    key: 'notifyReportReady',
    label: 'Report ready',
    description: 'When your advisor publishes a report on any service.',
    icon: FileText,
  },
  {
    key: 'notifyCallSummaries',
    label: 'Call summaries',
    description: 'A note whenever your advisor logs a call with you.',
    icon: PhoneCall,
  },
  {
    key: 'notifyServiceRecommendations',
    label: 'Service recommendations',
    description: 'When your advisor suggests a service based on what they see.',
    icon: Star,
  },
]

export function NotificationsCard() {
  const profileQuery = useMyInvestorProfile()
  const mutation = useUpdateMyNotificationPreferences()

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
          <Bell className="size-4" />
        </span>
        <div>
          <CardTitle className="text-base font-medium">Notifications</CardTitle>
          <CardDescription>
            Choose what your advisor&apos;s actions notify you about.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {profileQuery.isLoading || !profileQuery.data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          NOTIFICATION_TOGGLES.map((t) => (
            <div
              key={t.key}
              className="flex items-center justify-between gap-3 rounded-lg px-1 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                  <t.icon className="size-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t.label}</p>
                  <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                    {t.description}
                  </p>
                </div>
              </div>
              <Switch
                checked={profileQuery.data[t.key]}
                onCheckedChange={(checked) => mutation.mutate({ [t.key]: checked })}
                aria-label={t.label}
              />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  )
}
